import { useMemo } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useAuth } from '../../lib/AuthContext'
import { useThemeColors } from '../../lib/useThemeColors'
import { fonts } from '../../lib/theme'
import { useAvatars } from '../../lib/useAvatars'
import { resolvePerson } from '../../lib/personAvatar'
import { useSpecialJersey } from '../../lib/useSpecialJerseys'
import { lighten } from '../personAvatarConfig'
import PersonAvatar from '../PersonAvatar'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { legibleOn, textOn } from '../../lib/contrast'
import { useShowAvatars } from '../../lib/avatarPref'

const HEX = /^#[0-9A-Fa-f]{6}$/
const SIZE = 150       // lato dell'avatar intero (usa i livelli piccoli)
const BOX_W = 112, BOX_H = 138   // alto abbastanza da mostrare la medaglia sul petto
const X0 = 0.13        // ritaglio: busto e maglia ben visibili, si taglia sotto la vita

// Il leader di una classifica generale (tempo, punti o GPM) con la maglia di quella classifica.
// Come in gara: la stessa persona puo' avere piu maglie, quindi qui e' sempre il primo di quella classifica.
export default function LeaderHero({ userId, name, color, label, value, sub, pattern, medal, chipColor, keepJersey = false, onPress }) {
  const tc = useThemeColors()
  const { user, avatarSettings } = useAuth()
  const avatars = useAvatars([userId])
  const special = useSpecialJersey(userId)   // solo nelle gare di un giorno (keepJersey): nei giri vale la maglia della classifica
  const jersey = HEX.test(color ?? '') ? color : tc.rosaText

  const person = useMemo(() => {
    const src = userId === user?.id ? avatarSettings : avatars[userId]
    const p = resolvePerson(src, userId)
    if (keepJersey) {
      // gara di un giorno: nessuna maglia di classifica, l'avatar tiene i colori scelti dall'utente (o quelli di default)
      if (!(userId === user?.id ? avatarSettings : avatars[userId])?.person?.main) { p.main = jersey; p.accent = jersey; p.trim = jersey }
    } else if (pattern === 'dots') {
      // maglia a pois (GPM del Tour, della Vuelta): base bianca, pois e colletto nel colore GPM
      p.jersey = 5; p.main = '#FFFFFF'; p.accent = jersey; p.trim = jersey
    } else {
      p.main = jersey; p.accent = jersey; p.trim = jersey   // tinta unita, colletto compreso
    }
    return p
  }, [userId, user?.id, avatarSettings, avatars, jersey, pattern, keepJersey])

  const showAvatars = useShowAvatars()   // interruttore del Profilo: spento = niente avatar del leader
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${name}, ${value}`}
      style={[s.card, { backgroundColor: tc.bgCard2, borderColor: tc.border, borderLeftColor: legibleOn(jersey, tc.bgCard2, 1.6) }]}
    >
      {showAvatars && (
        <View style={[s.avatarBox, { backgroundColor: lighten(jersey, 0.82) }]}>
          <View style={{ marginLeft: -SIZE * X0 }}>
            <PersonAvatar person={person} size={SIZE} medal={medal} special={keepJersey ? special : null} />
          </View>
        </View>
      )}
      <View style={s.texts}>
        <View style={[s.chip, { backgroundColor: chipColor ?? jersey }]}>
          {medal && <MaterialCommunityIcons name="medal" size={13} color={textOn(chipColor ?? jersey)} />}
          {pattern === 'dots' && <MaterialCommunityIcons name="dots-grid" size={12} color={textOn(chipColor ?? jersey)} />}
          <Text style={[s.chipText, { color: textOn(chipColor ?? jersey) }]} numberOfLines={1}>{label}</Text>
        </View>
        <Text style={[s.name, { color: tc.text }]} numberOfLines={1}>{name}</Text>
        <Text style={[s.value, { color: tc.text }]}>{value}</Text>
        {!!sub && <Text style={[s.sub, { color: tc.textMuted }]} numberOfLines={1}>{sub}</Text>}
      </View>
    </TouchableOpacity>
  )
}

const s = StyleSheet.create({
  card: { flexDirection: 'row', alignItems: 'center', gap: 14, borderWidth: 1, borderLeftWidth: 4, borderRadius: 14, padding: 10, marginHorizontal: 12, marginBottom: 16 },
  avatarBox: { width: BOX_W, height: BOX_H, borderRadius: 12, overflow: 'hidden' },
  texts: { flex: 1, minWidth: 0, gap: 3 },
  chip: { alignSelf: 'flex-start', maxWidth: '100%', flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3, marginBottom: 2 },
  chipText: { color: '#111', fontFamily: fonts.condensedBold, fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' },
  name: { fontFamily: fonts.condensedBold, fontSize: 22 },
  value: { fontFamily: fonts.condensedBold, fontSize: 18 },
  sub: { fontSize: 12 },
})
